import type { PlanData, TaskNode } from "./types";
import { locateOrNull, isDescendant } from "./tree";
import { resyncEstimateTotals } from "./mutations";

// 3-way merge of two concurrent edits of the same plan. Strategy: take `local` as the working
// copy (its edits are inherently kept) and replay `remote`'s changes-since-`base` onto it, so
// non-conflicting concurrent edits combine. A conflict is recorded (and the site left as local)
// whenever both sides changed the SAME thing to DIFFERENT values (or delete-vs-edit / move
// clash). The caller auto-applies only when `conflicts` is empty, else falls back to the banner.

export interface MergeConflict { id?: string; field: string }
export interface MergeResult { data: PlanData; conflicts: MergeConflict[] }

interface Flat { node: TaskNode; parentId: string | null }
function index(tree: TaskNode[]): Map<string, Flat> {
  const m = new Map<string, Flat>();
  (function walk(list: TaskNode[], parentId: string | null) {
    for (const n of list) { m.set(n.id, { node: n, parentId }); walk(n.children, n.id); }
  })(tree, null);
  return m;
}

// scalar node fields that 3-way merge (estimate is derived when streams exist → re-synced last)
const NODE_SCALARS: (keyof TaskNode)[] = [
  "name", "version", "component", "jiraKey", "done", "disabled", "tag", "questions", "comment", "pinned", "complexity", "estimate",
];
// plan-level scalars (estimateView is a per-user view pref → keep local, don't conflict)
const PLAN_SCALARS: (keyof PlanData)[] = [
  "name", "showTags", "showVersions", "showComponents", "showDone", "showJira",
  "jiraUrl", "jiraDelim", "countDeps", "depDocOrder", "defaultEstimate", "showComplexity",
];

const numEq = (a: number | undefined, b: number | undefined) => a === b;

// does any own field of two nodes differ (children excluded)? — for delete/modify detection
function nodeFieldsDiffer(a: TaskNode, b: TaskNode): boolean {
  for (const f of NODE_SCALARS) if ((a as any)[f] !== (b as any)[f]) return true;
  if ((a.links || []).join(" ") !== (b.links || []).join(" ")) return true;
  const keys = new Set([...Object.keys(a.estimates || {}), ...Object.keys(b.estimates || {})]);
  for (const k of keys) if (!numEq(a.estimates?.[k], b.estimates?.[k])) return true;
  return false;
}

// union set-merge of a string list, keeping local order and appending remote-only entries
function unionList(local: string[], remote: string[]): string[] {
  const out = local.slice();
  for (const x of remote) if (!out.includes(x)) out.push(x);
  return out;
}
// 3-way set merge for links[]: keep local, add remote's new links, drop remote's removals
function mergeLinks(base: string[], local: string[], remote: string[]): string[] {
  const removes = base.filter((x) => !remote.includes(x)); // removed on remote
  const out = local.slice();
  for (const x of remote) if (!base.includes(x) && !out.includes(x)) out.push(x); // added on remote
  return out.filter((x) => !removes.includes(x));
}

function insertNode(tree: TaskNode[], node: TaskNode, parentId: string | null): void {
  if (!parentId) { tree.push(node); return; }
  const p = locateOrNull(tree, parentId);
  (p ? p.node.children : tree).push(node); // orphaned (parent gone) → attach to root
}
function removeNode(tree: TaskNode[], id: string): void {
  const loc = locateOrNull(tree, id);
  if (loc) loc.parentList.splice(loc.index, 1);
}
function applyMove(tree: TaskNode[], id: string, newParentId: string | null): boolean {
  if (newParentId && (newParentId === id || isDescendant(tree, id, newParentId))) return false; // cycle guard
  const loc = locateOrNull(tree, id);
  if (!loc) return false;
  const [n] = loc.parentList.splice(loc.index, 1);
  insertNode(tree, n, newParentId);
  return true;
}

export function mergePlans(base: PlanData, local: PlanData, remote: PlanData): MergeResult {
  const merged: PlanData = structuredClone(local);
  const conflicts: MergeConflict[] = [];
  const B = index(base.tree), R = index(remote.tree), L = index(local.tree);

  // ---- plan-level scalars: take remote's change unless local changed it too (differently) ----
  for (const f of PLAN_SCALARS) {
    if (remote[f] !== base[f]) {
      if (local[f] === base[f]) (merged as any)[f] = remote[f];
      else if (local[f] !== remote[f]) conflicts.push({ field: "plan." + String(f) });
    }
  }
  merged.versions = unionList(local.versions, remote.versions);
  merged.components = unionList(local.components, remote.components);
  merged.estimations = unionList(local.estimations, remote.estimations);
  merged.changes = Math.max(local.changes || 0, remote.changes || 0);
  merged.lastModified = [local.lastModified, remote.lastModified].filter(Boolean).sort().pop() || local.lastModified;

  // ---- replay remote's per-node field/stream/link/move changes onto merged (= local) ----
  for (const [id, rf] of R) {
    const bf = B.get(id);
    if (!bf) continue; // remote addition → handled below
    const mLoc = locateOrNull(merged.tree, id);
    if (!mLoc) { // local deleted a node remote still has
      if (nodeFieldsDiffer(bf.node, rf.node)) conflicts.push({ id, field: "deleted-locally" });
      continue;
    }
    const lf = L.get(id);
    // scalar fields ("estimate" is derived from the stream map when streams exist → skip it,
    // resyncEstimateTotals recomputes it from the merged estimates below)
    for (const f of NODE_SCALARS) {
      if (f === "estimate" && merged.estimations.length) continue;
      const bv = (bf.node as any)[f], rv = (rf.node as any)[f], lv = (mLoc.node as any)[f];
      if (rv !== bv) {
        if (lv === bv) (mLoc.node as any)[f] = rv;
        else if (lv !== rv) conflicts.push({ id, field: String(f) });
      }
    }
    // per-stream estimates map
    const streams = new Set([
      ...Object.keys(bf.node.estimates || {}), ...Object.keys(rf.node.estimates || {}), ...Object.keys(mLoc.node.estimates || {}),
    ]);
    for (const s of streams) {
      const bv = bf.node.estimates?.[s], rv = rf.node.estimates?.[s], lv = mLoc.node.estimates?.[s];
      if (!numEq(rv, bv)) {
        if (numEq(lv, bv)) { if (rv === undefined) delete mLoc.node.estimates[s]; else mLoc.node.estimates[s] = rv; }
        else if (!numEq(lv, rv)) conflicts.push({ id, field: "estimates." + s });
      }
    }
    // links (union semantics, never a conflict)
    mLoc.node.links = mergeLinks(bf.node.links || [], mLoc.node.links || [], rf.node.links || []);
    // move: remote changed parent
    if (rf.parentId !== bf.parentId) {
      const localMoved = !!lf && lf.parentId !== bf.parentId;
      if (!localMoved) { if (!applyMove(merged.tree, id, rf.parentId)) conflicts.push({ id, field: "moved" }); }
      else if (lf!.parentId !== rf.parentId) conflicts.push({ id, field: "moved" });
    }
  }

  // ---- remote additions (top-level of any added subtree; descendants ride along via clone) ----
  for (const [id, rf] of R) {
    if (B.has(id)) continue;
    if (rf.parentId && !B.has(rf.parentId)) continue; // inside a remote-added subtree
    if (locateOrNull(merged.tree, id)) continue;
    insertNode(merged.tree, structuredClone(rf.node), rf.parentId);
  }

  // ---- remote deletions (removed on remote; drop unless local modified it → conflict) ----
  for (const [id, bf] of B) {
    if (R.has(id)) continue;
    if (!locateOrNull(merged.tree, id)) continue; // local also deleted
    const lf = L.get(id);
    if (lf && nodeFieldsDiffer(bf.node, lf.node)) { conflicts.push({ id, field: "deleted-remotely" }); continue; }
    removeNode(merged.tree, id);
  }

  resyncEstimateTotals(merged.tree, merged.estimations); // restore estimate === Σ streams
  return { data: merged, conflicts };
}
